import { ReactNode, useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter'
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism'

export const CodeBlock = ({
    children,
    clipboard = true,
    language = '',
}: {
    children: ReactNode
    clipboard?: boolean
    language?: string
}) => {
    const ref = useRef<HTMLDivElement>(null)
    const [copied, setCopied] = useState(false)

    const copyToClipboard = () => {
        if (!ref.current || !navigator.clipboard || !children) return
        const text = typeof children === 'string' ? children : ref.current.textContent || ''
        navigator.clipboard.writeText(text)
        setCopied(true)
    }

    useEffect(() => {
        if (copied) {
            const timer = setTimeout(() => {
                setCopied(false)
            }, 2000)
            return () => clearTimeout(timer)
        }
    }, [copied])

    // Detect if children is a string for syntax highlighting
    const code = typeof children === 'string' ? children : ''

    return (
        <CodeBlockWrapper>
            {clipboard && (
                <button className="copy-button" onClick={copyToClipboard}>
                    <FontAwesomeIcon icon={copied ? ['fal', 'check'] : ['fal', 'clipboard']} />
                </button>
            )}
            <Pre>
                <div ref={ref}>
                    {typeof children === 'string' ? (
                        <SyntaxHighlighter
                            language={language}
                            style={vscDarkPlus}
                            customStyle={{ background: 'transparent', padding: 0, margin: 0 }}
                        >
                            {code}
                        </SyntaxHighlighter>
                    ) : (
                        <code>{children}</code>
                    )}
                </div>
            </Pre>
        </CodeBlockWrapper>
    )
}

export const CodeBlockWrapper = styled.div`
    margin: 0.75rem 0;
    width: 100%;
    overflow-x: auto;
    /* Hide scrollbar but keep functionality */
    scrollbar-width: none;  /* Firefox */
    -ms-overflow-style: none;  /* IE and Edge */
    &::-webkit-scrollbar {
        display: none;  /* Chrome, Safari and Opera */
    }
    position: relative;
    .copy-button {
        z-index: 10;
        position: absolute;
        cursor: pointer;
        top: 18px;
        right: 7px;
        outline: 0;
        color: white;
        transition: opacity 0.2s ease-in-out;
        border: ${props => props.theme.color.sidebar.gray3};
        width: 25px;
        height: 25px;
        border-radius: 50%;
        background-color: ${props => props.theme.color.sidebar.gray2};
        &:hover {
            opacity: 1;
        }
    }
`

const Pre = styled.pre`
    color: white;
    background-color: ${props => props.theme.color.sidebar.gray1};
    border: 1px solid ${props => props.theme.color.sidebar.gray3};
    padding: 15px 35px 15px 15px;
    border-radius: 6px;
    position: relative;
    word-break: break-all;
    white-space: pre-wrap;
    word-break: break-word;
    overflow-wrap: break-word;
    max-width: 100%;
    font-size: 0.95em;
    line-height: 1.4;
    margin:0;
`